/**
 * MeasurementTrendChart Component
 *
 * Displays trend visualization for body measurements over time
 * with date range selector and multi-series line chart.
 */

import React, { useState, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { TrendsResponse, WeightUnit, LengthUnit } from '../../types/measurements';
import { convertMeasurementValue } from '../../types/measurements';
import type { UnitSystem } from './UnitToggle';

interface MeasurementTrendChartProps {
  trendsData: TrendsResponse | undefined;
  isLoading?: boolean;
  displayUnit?: UnitSystem;
}

const MeasurementTrendChart: React.FC<MeasurementTrendChartProps> = ({
  trendsData,
  isLoading = false,
  displayUnit = 'metric',
}) => {
  const [selectedDays, setSelectedDays] = useState<number>(30);

  // Get target units based on display unit system
  const targetUnits = useMemo(() => ({
    weight: displayUnit === 'metric' ? 'kg' as WeightUnit : 'lbs' as WeightUnit,
    length: displayUnit === 'metric' ? 'cm' as LengthUnit : 'inches' as LengthUnit
  }), [displayUnit]);

  // Format chart data including optional measurements with unit conversion
  const chartData = useMemo(() => {
    if (!trendsData || trendsData.trend_data.length === 0) return [];

    return trendsData.trend_data.map((point) => ({
      date: new Date(point.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      weight: point.weight ? convertMeasurementValue(point.weight, targetUnits.weight).value : null,
      waist: point.waist ? convertMeasurementValue(point.waist, targetUnits.length).value : null,
      hip: point.hip ? convertMeasurementValue(point.hip, targetUnits.length).value : null,
      chest: point.chest ? convertMeasurementValue(point.chest, targetUnits.length).value : null,
      // Optional measurements - null will create gaps in the lines
      left_arm: point.left_arm ? convertMeasurementValue(point.left_arm, targetUnits.length).value : null,
      right_arm: point.right_arm ? convertMeasurementValue(point.right_arm, targetUnits.length).value : null,
      left_thigh: point.left_thigh ? convertMeasurementValue(point.left_thigh, targetUnits.length).value : null,
      right_thigh: point.right_thigh ? convertMeasurementValue(point.right_thigh, targetUnits.length).value : null,
    }));
  }, [trendsData, targetUnits]);

  // Check if any optional measurements exist in the data
  const hasOptionalData = useMemo(() => {
    if (!trendsData || trendsData.trend_data.length === 0) return false;
    return trendsData.trend_data.some(
      (point) =>
        point.left_arm || point.right_arm || point.left_thigh || point.right_thigh
    );
  }, [trendsData]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-2xl font-serif font-bold text-primary-700 mb-4">
          Measurement Trends
        </h2>
        <div className="animate-pulse">
          <div className="h-64 bg-neutral-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Handle insufficient data
  if (trendsData && trendsData.entry_count < 3) {
    return (
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-2xl font-serif font-bold text-primary-700 mb-4">
          Measurement Trends
        </h2>
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📈</div>
          <p className="text-neutral-600 text-lg mb-2">
            {trendsData.message || 'Not enough data for trends'}
          </p>
          <p className="text-neutral-500 text-sm">
            Record at least 3 measurements to see trend visualizations
          </p>
        </div>
      </div>
    );
  }

  // Handle no data
  if (!trendsData || chartData.length === 0) {
    return null;
  }

  const dateRangeOptions = [
    { label: '7 Days', value: 7 },
    { label: '30 Days', value: 30 },
    { label: '60 Days', value: 60 },
    { label: '90 Days', value: 90 },
  ];

  return (
    <div className="bg-white rounded-lg shadow-soft p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-serif font-bold text-primary-700">
          Measurement Trends
        </h2>

        {/* Date Range Selector */}
        <div className="flex gap-2">
          {dateRangeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedDays(option.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                selectedDays === option.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      {trendsData.statistics && Object.keys(trendsData.statistics).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {trendsData.statistics.weight && (
            <div className="bg-primary-50 rounded-lg p-4">
              <p className="text-xs text-neutral-600 mb-1">Weight</p>
              <p className="text-2xl font-bold text-primary-700">
                {(() => {
                  const converted = convertMeasurementValue(
                    { value: trendsData.statistics.weight.average, unit: trendsData.statistics.weight.unit },
                    targetUnits.weight
                  );
                  return (
                    <>
                      {converted.value}{' '}
                      <span className="text-sm font-normal">{targetUnits.weight}</span>
                    </>
                  );
                })()}
              </p>
              <p className="text-xs text-neutral-600 mt-1">
                {trendsData.statistics.weight.trend === 'increasing' && '↑ Increasing'}
                {trendsData.statistics.weight.trend === 'decreasing' && '↓ Decreasing'}
                {trendsData.statistics.weight.trend === 'stable' && '→ Stable'}
                {' '}
                ({trendsData.statistics.weight.change_percentage > 0 ? '+' : ''}
                {trendsData.statistics.weight.change_percentage}%)
              </p>
            </div>
          )}

          {trendsData.statistics.waist && (
            <div className="bg-secondary-50 rounded-lg p-4">
              <p className="text-xs text-neutral-600 mb-1">Waist</p>
              <p className="text-2xl font-bold text-secondary-700">
                {(() => {
                  const converted = convertMeasurementValue(
                    { value: trendsData.statistics.waist.average, unit: trendsData.statistics.waist.unit },
                    targetUnits.length
                  );
                  return (
                    <>
                      {converted.value}{' '}
                      <span className="text-sm font-normal">{targetUnits.length}</span>
                    </>
                  );
                })()}
              </p>
              <p className="text-xs text-neutral-600 mt-1">
                {trendsData.statistics.waist.trend === 'increasing' && '↑ Increasing'}
                {trendsData.statistics.waist.trend === 'decreasing' && '↓ Decreasing'}
                {trendsData.statistics.waist.trend === 'stable' && '→ Stable'}
              </p>
            </div>
          )}

          {trendsData.statistics.hip && (
            <div className="bg-primary-50 rounded-lg p-4">
              <p className="text-xs text-neutral-600 mb-1">Hip</p>
              <p className="text-2xl font-bold text-primary-700">
                {(() => {
                  const converted = convertMeasurementValue(
                    { value: trendsData.statistics.hip.average, unit: trendsData.statistics.hip.unit },
                    targetUnits.length
                  );
                  return (
                    <>
                      {converted.value}{' '}
                      <span className="text-sm font-normal">{targetUnits.length}</span>
                    </>
                  );
                })()}
              </p>
              <p className="text-xs text-neutral-600 mt-1">
                {trendsData.statistics.hip.trend === 'increasing' && '↑ Increasing'}
                {trendsData.statistics.hip.trend === 'decreasing' && '↓ Decreasing'}
                {trendsData.statistics.hip.trend === 'stable' && '→ Stable'}
              </p>
            </div>
          )}

          <div className="bg-neutral-100 rounded-lg p-4">
            <p className="text-xs text-neutral-600 mb-1">Entries</p>
            <p className="text-2xl font-bold text-neutral-700">
              {trendsData.entry_count}
            </p>
            <p className="text-xs text-neutral-600 mt-1">
              {selectedDays} day period
            </p>
          </div>
        </div>
      )}

      {/* Line Chart */}
      <div className="mt-6">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
            />
            <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: '14px', paddingTop: '20px' }}
            />
            <Line
              type="monotone"
              dataKey="weight"
              stroke="#C98CCC"
              strokeWidth={2}
              dot={{ fill: '#C98CCC', r: 4 }}
              name="Weight"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="waist"
              stroke="#B7A76D"
              strokeWidth={2}
              dot={{ fill: '#B7A76D', r: 4 }}
              name="Waist"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="hip"
              stroke="#9B8AC4"
              strokeWidth={2}
              dot={{ fill: '#9B8AC4', r: 4 }}
              name="Hip"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="chest"
              stroke="#A8C5A0"
              strokeWidth={2}
              dot={{ fill: '#A8C5A0', r: 4 }}
              name="Chest"
              connectNulls
            />
            {/* Optional measurements - only show if data exists */}
            {hasOptionalData && (
              <>
                <Line
                  type="monotone"
                  dataKey="left_arm"
                  stroke="#E8A87C"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#E8A87C', r: 3 }}
                  name="Left Arm"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="right_arm"
                  stroke="#D4A574"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#D4A574', r: 3 }}
                  name="Right Arm"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="left_thigh"
                  stroke="#C9A0DC"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#C9A0DC', r: 3 }}
                  name="Left Thigh"
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="right_thigh"
                  stroke="#B8A0C8"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#B8A0C8', r: 3 }}
                  name="Right Thigh"
                  connectNulls={false}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {hasOptionalData && (
        <p className="text-xs text-neutral-500 text-center mt-2">
          Dashed lines indicate optional measurements. Gaps appear where data is not recorded.
        </p>
      )}

      <p className="text-xs text-neutral-500 text-center mt-4">
        Showing data from {trendsData.date_range.start} to {trendsData.date_range.end}
      </p>
    </div>
  );
};

export default MeasurementTrendChart;
